import React, { useState } from "react";
import { useField, useFormikContext } from "formik";
import { useDispatch } from "react-redux";

// material
import {
  Box,
  Button,
  Container,
  Card,
  CardHeader,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Link,
  MenuItem,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  Stack
} from "@mui/material";

// components
import Page from "../../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import CancelIcon from "@mui/icons-material/Cancel";

// ----------------------------------------------------------------------

const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
};

export default function OutputSubmissionPage1(props) {
  const dispatch = useDispatch();
  const formikProps = useFormikContext();

  const [collaborator, setCollaborator] = useState("");

  const yesCollaboratorTop100 =
    "Yes, I have collaborator from Top 100 University Ranking or Subject Ranking.";
  const yesCollaboratorInternationalInstitute =
    "Yes, I have collaborator from Top International Institute.";
  const yesCollaboratorHighPerformanceProfessor =
    "Yes, I have collaborator who is a high performance professor but not coming from Top 100 University";

  const {
    formField: {
      howManyPublicationSubmitC2FReport,
      yourResearchField,
      publicationEvidence
    }
  } = props;

  const [
    howManyPublicationSubmitC2FReportField,
    howManyPublicationSubmitC2FReportMeta
  ] = useField(howManyPublicationSubmitC2FReport);
  const [yourResearchFieldField, yourResearchFieldMeta] =
    useField(yourResearchField);
  const [publicationEvidenceField, publicationEvidenceMeta] =
    useField(publicationEvidence);

  const handleRemoveClick = (index) => {
    const list = [...publicationEvidenceField.value];
    list.splice(index, 1);
    formikProps.setFieldValue(publicationEvidence.name, list);
  };

  const handleAddClick = () => {
    formikProps.setFieldValue(publicationEvidence.name, [
      ...publicationEvidenceField.value,
      {
        journalRanking: "",
        publicationStatus: "",
        typeResearchPublication: "",
        acceptedDate: "",
        publishedDate: "",
        doi: "",
        uploadResearchPublication: null,
        articleTitle: "",
        journalName: "",
        subjectAreaCategory: "",
        journalRankingPercentagePercentile: "",
        screenshotJournalRankingEvidence: null,
        firstAuthor: "",
        correspondingAuthor: "",
        collaboratorFromTop100: "",
        collaboratorName: "",
        universityInstituteCollaborator: "",
        referenceWorldRankingStatus: "",
        worldRankingYear: "",
        noWorldRanking: "",
        evidenceTopInternationalInstitute: null,
        evidenceHighPerformanceProfessor: null
      }
    ]);
  };

  return (
    <Page title="Output Submission">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Output Submission</Typography>
        </Box>
        <Stack
          direction="column"
          justifyContent="space-evenly"
          alignItems="stretch"
          spacing={4}
        >
          <Stack
            direction="column"
            justifyContent="space-evenly"
            alignItems="stretch"
            spacing={2}
          >
            <Stack direction="row" spacing={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {howManyPublicationSubmitC2FReport.label}
                </Typography>
                <TextField
                  name={howManyPublicationSubmitC2FReportField.name}
                  value={howManyPublicationSubmitC2FReportField.value}
                  onChange={(e) =>
                    howManyPublicationSubmitC2FReportField.onChange(e)
                  }
                  error={Boolean(
                    howManyPublicationSubmitC2FReportMeta.touched &&
                      howManyPublicationSubmitC2FReportMeta.error
                  )}
                  helperText={
                    howManyPublicationSubmitC2FReportMeta.touched &&
                    howManyPublicationSubmitC2FReportMeta.error
                  }
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {yourResearchField.label}
                </Typography>
                <TextField
                  select
                  variant="outlined"
                  name={yourResearchFieldField.name}
                  value={yourResearchFieldField.value}
                  onChange={(e) => yourResearchFieldField.onChange(e)}
                  error={Boolean(
                    yourResearchFieldMeta.touched && yourResearchFieldMeta.error
                  )}
                  helperText={
                    yourResearchFieldMeta.touched && yourResearchFieldMeta.error
                  }
                >
                  <MenuItem value={"Science field"}>Science field</MenuItem>
                  <MenuItem value={"Social Science and Humanities"}>
                    Social Science and Humanities
                  </MenuItem>
                </TextField>
              </FormControl>
            </Stack>

            <Typography variant="subtitle2">
              *Journal Ranking (Tier or Q) can be identified by 3 databases. You
              can choose 1 out of 3 to be an evidence of your publication:
            </Typography>

            <List>
              <ListItem>
                <ListItemText primary="1. Scimago Journal Rank (SJR)" />
              </ListItem>
              <ListItem>
                <Typography variant="body1">
                  Searching guide please see page 1-8 in{" "}
                  <Link href="https://shorturl.at/vzD14">
                    https://shorturl.at/vzD14
                  </Link>
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Journal Citation Report /Journal Impact Factor (Web of Science)" />
              </ListItem>
              <ListItem>
                <Typography variant="body1">
                  Searching guide please see page 9-12 in{" "}
                  <Link href="https://shorturl.atIxGLQ">
                    https://shorturl.atIxGLQ
                  </Link>
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemText primary="3. CiteScore Rank (Scopus)" />
              </ListItem>
              <ListItem>
                <Typography variant="body1">
                  Searching guide as follows:
                </Typography>
              </ListItem>

              <ListItem>
                <Typography variant="body1">
                  1) Click the link{" "}
                  <Link href="https://bit.!y/2wNumhx">
                    https://bit.!y/2wNumhx
                  </Link>
                </Typography>
              </ListItem>

              <ListItem>
                <Typography variant="body1">
                  2) Type journal name in "Enter title"then click "find sources"
                </Typography>
              </ListItem>
            </List>

            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="subtitle2">
                Screenshots' example of journal ranking result from each
                databases
              </Typography>
              <Typography>
                To see a bigger picture, right click at the picture and choose
                'open picture in new tab.
              </Typography>

              <Stack direction="row" spacing={6} sx={{ paddingTop: 6 }}>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="subtitle2">
                    Scimago Journal Rank
                  </Typography>
                  <img
                    src="/member/static/images/outputSubmission/Scimago_evidence.png"
                    alt="Scimago_evidence"
                    width="300"
                    height="250"
                  />
                </Stack>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="subtitle2">
                    Journal Citation Report /
                  </Typography>
                  <Typography variant="subtitle2">
                    Journal Impact Factor (Web of Science)
                  </Typography>
                  <img
                    src="/member/static/images/outputSubmission/JCR_Eveidence.png"
                    alt="JCR_Eveidence"
                    width="500"
                    height="250"
                  />
                </Stack>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="subtitle2">
                    CiteScore Rank (Scopus)
                  </Typography>
                  <img
                    src="/member/static/images/outputSubmission/Citescore_evidence.png"
                    alt="Citescore_evidence"
                    width="300"
                    height="250"
                  />
                </Stack>
              </Stack>
            </Grid>

            <Typography variant="h5">Publication Evidence</Typography>

            {publicationEvidenceField.value?.map((x, i) => {
              return (
                <Box key={i}>
                  <Card sx={{ backgroundColor: "#F9F9F9" }}>
                    <CardHeader
                      title={`Publication ${i + 1}`}
                      action={
                        i !== 0 && (
                          <IconButton
                            aria-label="settings"
                            onClick={() => handleRemoveClick(i)}
                            sx={{ color: "#DE5C8E" }}
                          >
                            <CancelIcon />
                          </IconButton>
                        )
                      }
                    />
                    <CardContent>
                      <Stack spacing={4}>
                        <Stack direction="row">
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]?.journalRanking
                                  ?.label
                              }
                            </Typography>
                            <RadioGroup
                              row
                              name={
                                publicationEvidence.subMenu[0].journalRanking
                                  .name
                              }
                              value={
                                publicationEvidenceField.value[i].journalRanking
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.journalRanking`,
                                  e.target.value
                                );
                              }}
                            >
                              <FormControlLabel
                                value="tier1"
                                control={<Radio />}
                                label="Tier 1"
                              />
                              <FormControlLabel
                                value="q1"
                                control={<Radio />}
                                label="Q1"
                              />
                              <FormControlLabel
                                value="q2"
                                control={<Radio />}
                                label="Q2"
                              />
                            </RadioGroup>
                            {publicationEvidenceMeta.error
                              ? publicationEvidenceMeta.touched && (
                                  <span
                                    style={{
                                      color: "#FF4842",
                                      lineHeight: 1.5,
                                      fontSize: "0.75rem",
                                      fontFamily: "sans-serif",
                                      fontWeight: 400,
                                      textAlign: "left",
                                      marginTop: "3px",
                                      marginRight: "14px",
                                      marginBottom: 0,
                                      marginLeft: "14px"
                                    }}
                                  >
                                    {
                                      publicationEvidenceMeta.error[i]
                                        ?.journalRanking
                                    }
                                  </span>
                                )
                              : ""}
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.publicationStatus?.label
                              }
                            </Typography>
                            <RadioGroup
                              row
                              name={
                                publicationEvidence.subMenu[0].publicationStatus
                                  .name
                              }
                              value={
                                publicationEvidenceField.value[i]
                                  .publicationStatus
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.publicationStatus`,
                                  e.target.value
                                );
                              }}
                            >
                              <FormControlLabel
                                value="accepted"
                                control={<Radio />}
                                label="Accepted"
                              />
                              <FormControlLabel
                                value="published"
                                control={<Radio />}
                                label="Published"
                              />
                            </RadioGroup>
                            {publicationEvidenceMeta.error
                              ? publicationEvidenceMeta.touched && (
                                  <span
                                    style={{
                                      color: "#FF4842",
                                      lineHeight: 1.5,
                                      fontSize: "0.75rem",
                                      fontFamily: "sans-serif",
                                      fontWeight: 400,
                                      textAlign: "left",
                                      marginTop: "3px",
                                      marginRight: "14px",
                                      marginBottom: 0,
                                      marginLeft: "14px"
                                    }}
                                  >
                                    {
                                      publicationEvidenceMeta.error[i]
                                        ?.publicationStatus
                                    }
                                  </span>
                                )
                              : ""}
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.typeResearchPublication?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0]
                                  .typeResearchPublication.name
                              }
                              value={
                                publicationEvidenceField.value[i]
                                  .typeResearchPublication
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.typeResearchPublication`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.typeResearchPublication
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.typeResearchPublication
                                  : ""
                              }
                            />
                            <Typography variant="body1">
                              E.g. research article, original research, etc.
                            </Typography>
                          </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={6}>
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]?.acceptedDate
                                  ?.label
                              }
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                inputFormat="dd/MM/yyyy"
                                name={
                                  publicationEvidence.subMenu[0].acceptedDate
                                    .name
                                }
                                value={
                                  publicationEvidenceField.value[i].acceptedDate
                                }
                                onChange={(date) => {
                                  if (isValidDate(date)) {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.acceptedDate`,
                                      date.toISOString()
                                    );
                                  } else {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.acceptedDate`,
                                      date
                                    );
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={Boolean(
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                            publicationEvidenceMeta.error[i]
                                              ?.acceptedDate
                                        : false
                                    )}
                                    helperText={
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.acceptedDate
                                        : ""
                                    }
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]?.publishedDate
                                  ?.label
                              }
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                inputFormat="dd/MM/yyyy"
                                name={
                                  publicationEvidence.subMenu[0].publishedDate
                                    .name
                                }
                                value={
                                  publicationEvidenceField.value[i]
                                    .publishedDate
                                }
                                onChange={(date) => {
                                  if (isValidDate(date)) {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.publishedDate`,
                                      date.toISOString()
                                    );
                                  } else {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.publishedDate`,
                                      date
                                    );
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={Boolean(
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                            publicationEvidenceMeta.error[i]
                                              ?.publishedDate
                                        : false
                                    )}
                                    helperText={
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.publishedDate
                                        : ""
                                    }
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={6}>
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {publicationEvidence.subMenu[0]?.doi?.label}
                            </Typography>
                            <TextField
                              name={publicationEvidence.subMenu[0].doi.name}
                              value={publicationEvidenceField.value[i].doi}
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.doi`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]?.doi
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]?.doi
                                  : ""
                              }
                            />
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.uploadResearchPublication?.label
                              }
                            </Typography>
                            <TextField
                              inputProps={{
                                accept: "application/pdf"
                              }}
                              type="file"
                              name={
                                publicationEvidence.subMenu[0]
                                  .uploadResearchPublication.name
                              }
                              onChange={(event) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.uploadResearchPublication.file`,
                                  event.currentTarget.files[0]
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.uploadResearchPublication
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.uploadResearchPublication
                                  : ""
                              }
                            />
                            <Typography variant="body1">.pdf only</Typography>
                          </FormControl>
                        </Stack>

                        <FormControl fullWidth margin={"normal"}>
                          <Typography variant="subtitle2">
                            {
                              publicationEvidence.subMenu[0]?.articleTitle
                                ?.label
                            }
                          </Typography>
                          <TextField
                            multiline
                            rows={5}
                            name={
                              publicationEvidence.subMenu[0].articleTitle.name
                            }
                            value={
                              publicationEvidenceField.value[i].articleTitle
                            }
                            onChange={(e) => {
                              formikProps.setFieldValue(
                                `publicationEvidence.${i}.articleTitle`,
                                e.target.value
                              );
                            }}
                            error={Boolean(
                              publicationEvidenceMeta.error
                                ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.articleTitle
                                : false
                            )}
                            helperText={
                              publicationEvidenceMeta.error
                                ? publicationEvidenceMeta.touched &&
                                  publicationEvidenceMeta.error[i]?.articleTitle
                                : ""
                            }
                          />
                        </FormControl>

                        <Stack direction="row" spacing={6}>
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]?.journalName
                                  ?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0].journalName.name
                              }
                              value={
                                publicationEvidenceField.value[i].journalName
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.journalName`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.journalName
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.journalName
                                  : ""
                              }
                            />
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.subjectAreaCategory?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0]
                                  .subjectAreaCategory.name
                              }
                              value={
                                publicationEvidenceField.value[i]
                                  .subjectAreaCategory
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.subjectAreaCategory`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.subjectAreaCategory
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.subjectAreaCategory
                                  : ""
                              }
                            />
                          </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={6}>
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.journalRankingPercentagePercentile?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0]
                                  .journalRankingPercentagePercentile.name
                              }
                              value={
                                publicationEvidenceField.value[i]
                                  .journalRankingPercentagePercentile
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.journalRankingPercentagePercentile`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.journalRankingPercentagePercentile
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.journalRankingPercentagePercentile
                                  : ""
                              }
                            />
                            <Typography variant="body1">
                              Calculate: Journal ranking percentage = (no. of
                              journal rank / total no. of journal in the
                              area)*100
                            </Typography>
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.screenshotJournalRankingEvidence?.label
                              }
                            </Typography>
                            <TextField
                              inputProps={{
                                accept:
                                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                              }}
                              type="file"
                              name={
                                publicationEvidence.subMenu[0]
                                  .screenshotJournalRankingEvidence.name
                              }
                              onChange={(event) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.screenshotJournalRankingEvidence.file`,
                                  event.currentTarget.files[0]
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.screenshotJournalRankingEvidence
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.screenshotJournalRankingEvidence
                                  : ""
                              }
                            />
                            <Typography variant="body1">
                              See the example of a screenshot from guideline
                              above
                            </Typography>
                          </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={6}>
                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]?.firstAuthor
                                  ?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0].firstAuthor.name
                              }
                              value={
                                publicationEvidenceField.value[i].firstAuthor
                              }
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  `publicationEvidence.${i}.firstAuthor`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.firstAuthor
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.firstAuthor
                                  : ""
                              }
                            />
                            <Typography>
                              Required to be a PhD grantee
                            </Typography>
                          </FormControl>

                          <FormControl fullWidth>
                            <Typography variant="subtitle2">
                              {
                                publicationEvidence.subMenu[0]
                                  ?.correspondingAuthor?.label
                              }
                            </Typography>
                            <TextField
                              name={
                                publicationEvidence.subMenu[0]
                                  .correspondingAuthor.name
                              }
                              value={
                                publicationEvidenceField.value[i]
                                  .correspondingAuthor
                              }
                              onChange={async (e) => {
                                await formikProps.setFieldValue(
                                  `publicationEvidence.${i}.correspondingAuthor`,
                                  e.target.value
                                );
                              }}
                              error={Boolean(
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.correspondingAuthor
                                  : false
                              )}
                              helperText={
                                publicationEvidenceMeta.error
                                  ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.correspondingAuthor
                                  : ""
                              }
                            />
                            <Typography>Required to be a mentor</Typography>
                          </FormControl>
                        </Stack>

                        <FormControl fullWidth>
                          <Typography variant="subtitle2">
                            {
                              publicationEvidence.subMenu[0]
                                ?.collaboratorFromTop100?.label
                            }
                          </Typography>
                          <TextField
                            select
                            variant="outlined"
                            name={
                              publicationEvidence.subMenu[0]
                                .collaboratorFromTop100.name
                            }
                            value={
                              publicationEvidenceField.value[i]
                                .collaboratorFromTop100
                            }
                            onChange={(e) => {
                              formikProps.setFieldValue(
                                `publicationEvidence.${i}.collaboratorFromTop100`,
                                e.target.value
                              );

                              switch (true) {
                                case e.target.value ===
                                  yesCollaboratorInternationalInstitute ||
                                  e.target.value ===
                                    yesCollaboratorHighPerformanceProfessor:
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.collaboratorName`,
                                    ""
                                  );
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.universityInstituteCollaborator`,
                                    ""
                                  );
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.referenceWorldRankingStatus`,
                                    ""
                                  );
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.worldRankingYear`,
                                    ""
                                  );
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.noWorldRanking`,
                                    ""
                                  );
                                  break;
                                case e.target.value === yesCollaboratorTop100 ||
                                  e.target.value ===
                                    yesCollaboratorHighPerformanceProfessor:
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.evidenceTopInternationalInstitute`,
                                    null
                                  );
                                  break;
                                case e.target.value === yesCollaboratorTop100 ||
                                  e.target.value ===
                                    yesCollaboratorInternationalInstitute:
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.evidenceHighPerformanceProfessor`,
                                    null
                                  );
                                  break;
                                default:
                                  break;
                              }
                            }}
                            error={Boolean(
                              publicationEvidenceMeta.error
                                ? publicationEvidenceMeta.touched &&
                                    publicationEvidenceMeta.error[i]
                                      ?.collaboratorFromTop100
                                : false
                            )}
                            helperText={
                              publicationEvidenceMeta.error
                                ? publicationEvidenceMeta.touched &&
                                  publicationEvidenceMeta.error[i]
                                    ?.collaboratorFromTop100
                                : ""
                            }
                          >
                            <MenuItem value={yesCollaboratorTop100}>
                              Yes, I have collaborator from Top 100 University
                              Ranking or Subject Ranking.
                            </MenuItem>
                            <MenuItem
                              value={yesCollaboratorInternationalInstitute}
                            >
                              Yes, I have collaborator from Top International
                              Institute.
                            </MenuItem>
                            <MenuItem
                              value={yesCollaboratorHighPerformanceProfessor}
                            >
                              Yes, I have collaborator who is a high performance
                              professor but not coming from Top 100 University
                            </MenuItem>
                            <MenuItem value={"No, I don't have."}>
                              No, I don't have.
                            </MenuItem>
                          </TextField>
                        </FormControl>

                        {publicationEvidenceField.value[i]
                          .collaboratorFromTop100 === yesCollaboratorTop100 ? (
                          <Stack spacing={4}>
                            <Stack direction="row" spacing={6}>
                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .collaboratorName.label
                                  }
                                </Typography>
                                <TextField
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .collaboratorName.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .collaboratorName
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.collaboratorName`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.collaboratorName
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.collaboratorName
                                      : ""
                                  }
                                />
                              </FormControl>

                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .universityInstituteCollaborator.label
                                  }
                                </Typography>
                                <TextField
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .universityInstituteCollaborator.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .universityInstituteCollaborator
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.universityInstituteCollaborator`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.universityInstituteCollaborator
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.universityInstituteCollaborator
                                      : ""
                                  }
                                />
                              </FormControl>
                            </Stack>

                            <Stack direction="row" spacing={6}>
                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .referenceWorldRankingStatus.label
                                  }
                                </Typography>
                                <TextField
                                  select
                                  variant="outlined"
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .referenceWorldRankingStatus.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .referenceWorldRankingStatus
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.referenceWorldRankingStatus`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.referenceWorldRankingStatus
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.referenceWorldRankingStatus
                                      : ""
                                  }
                                >
                                  <MenuItem value={"QS University Ranking"}>
                                    QS University Ranking
                                  </MenuItem>
                                  <MenuItem value={"QS Subject Ranking"}>
                                    QS Subject Ranking
                                  </MenuItem>
                                  <MenuItem value={"THE University Ranking"}>
                                    THE University Ranking
                                  </MenuItem>
                                  <MenuItem value={"THE Subject Ranking"}>
                                    THE Subject Ranking
                                  </MenuItem>
                                </TextField>
                              </FormControl>

                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .worldRankingYear.label
                                  }
                                </Typography>
                                <TextField
                                  select
                                  variant="outlined"
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .worldRankingYear.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .worldRankingYear
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.worldRankingYear`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.worldRankingYear
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.worldRankingYear
                                      : ""
                                  }
                                >
                                  <MenuItem value={"2019"}>2019</MenuItem>
                                  <MenuItem value={"2020"}>2020</MenuItem>
                                  <MenuItem value={"2021"}>2021</MenuItem>
                                </TextField>
                              </FormControl>

                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .noWorldRanking.label
                                  }
                                </Typography>
                                <TextField
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .noWorldRanking.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .noWorldRanking
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.noWorldRanking`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.noWorldRanking
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.noWorldRanking
                                      : ""
                                  }
                                />
                              </FormControl>
                            </Stack>
                          </Stack>
                        ) : publicationEvidenceField.value[i]
                            .collaboratorFromTop100 ===
                          yesCollaboratorInternationalInstitute ? (
                          <Stack spacing={4}>
                            <Stack direction="row" spacing={6}>
                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .collaboratorName.label
                                  }
                                </Typography>
                                <TextField
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .collaboratorName.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .collaboratorName
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.collaboratorName`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.collaboratorName
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.collaboratorName
                                      : ""
                                  }
                                />
                              </FormControl>

                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .universityInstituteCollaborator.label
                                  }
                                </Typography>
                                <TextField
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .universityInstituteCollaborator.name
                                  }
                                  value={
                                    publicationEvidenceField.value[i]
                                      .universityInstituteCollaborator
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.universityInstituteCollaborator`,
                                      e.target.value
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.universityInstituteCollaborator
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.universityInstituteCollaborator
                                      : ""
                                  }
                                />
                              </FormControl>
                            </Stack>

                            <FormControl fullWidth>
                              <Typography variant="subtitle2">
                                {
                                  publicationEvidence.subMenu[0]
                                    .evidenceTopInternationalInstitute.label
                                }
                              </Typography>
                              <TextField
                                inputProps={{
                                  accept:
                                    "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                                }}
                                type="file"
                                name={
                                  publicationEvidence.subMenu[0]
                                    .evidenceTopInternationalInstitute.name
                                }
                                onChange={(event) => {
                                  formikProps.setFieldValue(
                                    `publicationEvidence.${i}.evidenceTopInternationalInstitute.file`,
                                    event.currentTarget.files[0]
                                  );
                                }}
                                error={Boolean(
                                  publicationEvidenceMeta.error
                                    ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.evidenceTopInternationalInstitute
                                    : false
                                )}
                                helperText={
                                  publicationEvidenceMeta.error
                                    ? publicationEvidenceMeta.touched &&
                                      publicationEvidenceMeta.error[i]
                                        ?.evidenceTopInternationalInstitute
                                    : ""
                                }
                              />
                              <Typography>
                                The evidence may collect from trustworthy
                                sources such as Scimago Instution Ranking etc.
                              </Typography>
                            </FormControl>
                          </Stack>
                        ) : (
                          publicationEvidenceField.value[i]
                            .collaboratorFromTop100 ===
                            yesCollaboratorHighPerformanceProfessor && (
                            <Stack spacing={4}>
                              <Stack direction="row" spacing={6}>
                                <FormControl fullWidth>
                                  <Typography variant="subtitle2">
                                    {
                                      publicationEvidence.subMenu[0]
                                        .collaboratorName.label
                                    }
                                  </Typography>
                                  <TextField
                                    name={
                                      publicationEvidence.subMenu[0]
                                        .collaboratorName.name
                                    }
                                    value={
                                      publicationEvidenceField.value[i]
                                        .collaboratorName
                                    }
                                    onChange={(e) => {
                                      formikProps.setFieldValue(
                                        `publicationEvidence.${i}.collaboratorName`,
                                        e.target.value
                                      );
                                    }}
                                    error={Boolean(
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                            publicationEvidenceMeta.error[i]
                                              ?.collaboratorName
                                        : false
                                    )}
                                    helperText={
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.collaboratorName
                                        : ""
                                    }
                                  />
                                </FormControl>

                                <FormControl fullWidth>
                                  <Typography variant="subtitle2">
                                    {
                                      publicationEvidence.subMenu[0]
                                        .universityInstituteCollaborator.label
                                    }
                                  </Typography>
                                  <TextField
                                    name={
                                      publicationEvidence.subMenu[0]
                                        .universityInstituteCollaborator.name
                                    }
                                    value={
                                      publicationEvidenceField.value[i]
                                        .universityInstituteCollaborator
                                    }
                                    onChange={(e) => {
                                      formikProps.setFieldValue(
                                        `publicationEvidence.${i}.universityInstituteCollaborator`,
                                        e.target.value
                                      );
                                    }}
                                    error={Boolean(
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                            publicationEvidenceMeta.error[i]
                                              ?.universityInstituteCollaborator
                                        : false
                                    )}
                                    helperText={
                                      publicationEvidenceMeta.error
                                        ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.universityInstituteCollaborator
                                        : ""
                                    }
                                  />
                                </FormControl>
                              </Stack>

                              <FormControl fullWidth>
                                <Typography variant="subtitle2">
                                  {
                                    publicationEvidence.subMenu[0]
                                      .evidenceHighPerformanceProfessor.label
                                  }
                                </Typography>
                                <TextField
                                  inputProps={{
                                    accept:
                                      "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                                  }}
                                  type="file"
                                  name={
                                    publicationEvidence.subMenu[0]
                                      .evidenceHighPerformanceProfessor.name
                                  }
                                  onChange={(event) => {
                                    formikProps.setFieldValue(
                                      `publicationEvidence.${i}.evidenceHighPerformanceProfessor.file`,
                                      event.currentTarget.files[0]
                                    );
                                  }}
                                  error={Boolean(
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                          publicationEvidenceMeta.error[i]
                                            ?.evidenceHighPerformanceProfessor
                                      : false
                                  )}
                                  helperText={
                                    publicationEvidenceMeta.error
                                      ? publicationEvidenceMeta.touched &&
                                        publicationEvidenceMeta.error[i]
                                          ?.evidenceHighPerformanceProfessor
                                      : ""
                                  }
                                />
                                <Typography>
                                  You can show his/her high performace track
                                  record such as H-index, FWCI, Publications,
                                  etc.
                                </Typography>
                              </FormControl>
                            </Stack>
                          )
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                  <Box sx={{ paddingTop: 3 }}>
                    {publicationEvidenceField.value?.length - 1 === i && (
                      <Button
                        onClick={handleAddClick}
                        variant="contained"
                        size="large"
                      >
                        Add More Publication
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
}
